import cn from 'classnames';
import Spinner from '@creditflowresearch/uilib/Loader/Spinner';

import type { IButtonProps, IIconSlotProps } from './types';
import styles from './button.module.scss';
import {
  variantClassMap,
  colorClassMap,
  sizeClassMap,
  defaultIconSize,
  spinnerSizeMap,
  fitClassMap,
} from './constants';
import { useButtonGroupContext } from '../ButtonGroup/ButtonGroupContext';
import resolveProps from '../utils/resolveProps';
import { generateResponsiveClasses, capitalize } from '../shared/responsive-classes';

const IconSlot = ({ icon, iconSize, loaderSize, loading }: IIconSlotProps) => (
  <span className={ styles.iconWrp } style={ { width: iconSize, height: iconSize } }>
    { loading ? <Spinner size={ loaderSize } color="currentColor" /> : icon }
  </span>
);

const Button = <E extends React.ElementType = 'button'>({
  as = 'button' as E,
  ref,
  type = 'button',
  variant,
  size,
  color,
  iconPosition = 'left',
  icon,
  iconSize,
  loading = false,
  disabled = false,
  fit,
  children,
  childrenClassName,
  className,
  dataTest,
  ariaLabel,
  ...restProps
}: IButtonProps<E>) => {
  // props priority: `inProps` > `contextProps`
  const contextProps = useButtonGroupContext();
  const resolved = resolveProps(contextProps, { variant, size, color });
  const resolvedVariant = resolved.variant ?? 'solid';
  const resolvedSize = resolved.size ?? 'M';
  const resolvedColor = resolved.color ?? 'primary';

  // `as` is generic (E); widen for JSX so the spread props type-check.
  const Component = as as React.ElementType;
  const isNativeButton = as === 'button';
  const resolvedIconSize = iconSize || defaultIconSize[resolvedSize];
  const loaderSize = spinnerSizeMap[resolvedSize];
  const hasIcon = Boolean(icon);
  const isIconOnly = hasIcon && !children;
  const hasCenteredLoading = loading && !hasIcon;
  const isDisabled = disabled || loading;
  const elementProps = isNativeButton ? { type, disabled: isDisabled } : {};

  return (
    <Component
      ref={ ref }
      className={ cn(
        styles.btn,
        sizeClassMap[resolvedSize],
        variantClassMap[resolvedVariant],
        colorClassMap[resolvedColor],
        {
          [styles.iconOnly]: isIconOnly,
          [styles.disabled]: isDisabled,
        },
        generateResponsiveClasses(fit, 'fit', fitClassMap, capitalize),
        className,
      ) }
      aria-label={ ariaLabel }
      data-test={ dataTest }
      { ...restProps }
      { ...elementProps }
    >
      <span className={ cn(styles.content, {
        [styles.contentHidden]: hasCenteredLoading,
      }) }
      >
        { hasIcon && iconPosition === 'left' && (
          <IconSlot
            icon={ icon }
            iconSize={ resolvedIconSize }
            loaderSize={ loaderSize }
            loading={ loading }
          />
        ) }
        { children && <span className={ childrenClassName }>{ children }</span> }
        { hasIcon && iconPosition === 'right' && (
          <IconSlot
            icon={ icon }
            iconSize={ resolvedIconSize }
            loaderSize={ loaderSize }
            loading={ loading }
          />
        ) }
      </span>
      { hasCenteredLoading && (
        <span className={ styles.spinnerWrp }>
          <IconSlot
            icon={ icon }
            iconSize={ resolvedIconSize }
            loaderSize={ loaderSize }
            loading
          />
        </span>
      ) }
    </Component>
  );
};

export default Button;
